/**
 * PB-AIT-2/9 — AI Text field plugin (registry adapter + lifecycle).
 */

import {
  computeAiTextReadiness,
  hasAiTextSettingsSignature,
  isAiTextFieldType,
  parseAiTextFieldSettings,
  resolveAiTextLayoutMode,
  AI_TEXT_LAYOUT_CONTENT_ONLY,
} from '@theme/pb-ai-text-logic';
import {
  bindSelectionsChangedListener,
  initWrap,
  flushWrapFromDom,
  validateStep as validateAiTextStep,
} from '@theme/pb-ai-text-runtime';

import {
  deserializeAiTextFieldState,
  resolveAiTextDraftForContext,
} from '@theme/pb-dl-ai-hydrate';

function isAiTextField(field) {
  return isAiTextFieldType(field) || hasAiTextSettingsSignature(field);
}

export function renderAiTextPreviewStage(productImageUrl) {
  const bg = productImageUrl ? String(productImageUrl).trim() : '';
  const bgStyle = bg
    ? ' style="--pb-ai-text-bg: url(\'' + bg.replace(/'/g, '%27') + '\');"'
    : '';
  return (
    '<div class="pb-ai-text-preview-stage pb-ai-text-preview--accent-type"' +
    bgStyle +
    '>' +
    '<div class="pb-ai-text-preview-bg" aria-hidden="true"></div>' +
    '<div class="pb-ai-text-preview-body">' +
    '<div class="pb-ai-text-preview-loader" style="display:none;" aria-hidden="true">' +
    '<div class="pb-ai-text-preview-loader-inner">' +
    '<span class="pb-ai-text-preview-spinner"></span>' +
    '<p class="pb-ai-text-preview-loader-msg pb-preview-display pb-preview-display--secondary">Creating your text…</p>' +
    '</div></div>' +
    '<div class="pb-ai-text-preview-error" style="display:none;" aria-live="polite">' +
    '<p class="pb-ai-text-preview-error-msg"></p>' +
    '<button type="button" class="builder-btn meta-btn pb-ai-text-preview-retry" style="display:none;">Try again</button>' +
    '<button type="button" class="builder-btn meta-btn pb-ai-text-preview-skip" hidden>Continue without this text</button>' +
    '</div>' +
    '<p class="pb-ai-text-preview-placeholder">Your personalized text will appear here.</p>' +
    '<p class="pb-ai-text-preview-text pb-preview-display" style="display:none;" aria-live="polite"></p>' +
    '</div></div>'
  );
}

/**
 * @param {HTMLElement} wrap — .pb-ai-text-wrap
 * @param {{ reviewAcknowledged?: boolean } | null} [options]
 */
export function getFieldReadiness(wrap, options) {
  if (!wrap) {
    return {
      stepNavigable: true,
      finalizeReady: true,
      jobStatus: 'idle',
      textReady: false,
      required: false,
    };
  }
  const state = wrap._pbAiTextState || {};
  let cfg = {};
  try {
    cfg = parseAiTextFieldSettings(wrap.dataset.settings || '');
  } catch (e) {
    cfg = {};
  }
  let reviewAcknowledged =
    options && options.reviewAcknowledged != null ? !!options.reviewAcknowledged : false;
  if (
    !reviewAcknowledged &&
    typeof window !== 'undefined' &&
    window.PBAiTextReview &&
    typeof window.PBAiTextReview.isReviewAcknowledged === 'function'
  ) {
    reviewAcknowledged = window.PBAiTextReview.isReviewAcknowledged(
      window.builderState,
      wrap.dataset?.label,
      state
    );
  }
  return computeAiTextReadiness(state, cfg, { reviewAcknowledged });
}

/**
 * @param {object} deps — shared field-runtime helpers
 */
export function createAiTextAdapter(deps) {
  const {
    fieldTypeLower: fieldTypeLowerFn,
    resolveEsc,
    resolveSelections,
    resolveProductRef,
    settingsToString,
    renderControlShell,
    delegateInitStep,
  } = deps;

  bindSelectionsChangedListener();

  return {
    id: 'ai-text',
    matches(field) {
      if (hasAiTextSettingsSignature(field)) return true;
      return fieldTypeLowerFn(field).includes('ai text');
    },
    renderStep(ctx) {
      const field = ctx.field;
      const esc = resolveEsc(ctx);
      const settingsStr = settingsToString(field.settings);
      const cfg = parseAiTextFieldSettings(field.settings);
      const productRef = resolveProductRef(ctx);
      const productImage =
        cfg.background_image ||
        productRef.image ||
        (typeof window !== 'undefined' ? window.BUILDER_DATA?.mainProduct?.image : '') ||
        '';
      const existing = resolveSelections(ctx)[field.label];
      const existingText =
        existing != null && typeof existing === 'string' ? String(existing).trim() : '';
      const layoutMode = resolveAiTextLayoutMode(cfg);
      const isContentOnly = layoutMode === AI_TEXT_LAYOUT_CONTENT_ONLY;
      const hiddenInput =
        '<input type="hidden" class="builder-required pb-ai-text-hidden" data-label="' +
        esc(field.label) +
        '" data-required="' +
        (cfg.required ? 'true' : 'false') +
        '" data-valid="' +
        (existingText ? 'true' : 'false') +
        '" value="' +
        esc(existingText) +
        '">';
      let innerHtml =
        '<div class="pb-ai-text-wrap" data-label="' +
        esc(field.label) +
        '" data-settings="' +
        esc(settingsStr) +
        '" data-layout-mode="' +
        esc(layoutMode) +
        '"' +
        (productImage ? ' data-product-image="' + esc(productImage) + '"' : '') +
        '>';
      if (!isContentOnly) {
        innerHtml +=
          '<div class="pb-ai-text-field-status" role="status" aria-live="polite"></div>' +
          (cfg.editable
            ? '<textarea class="pb-ai-text-editable-input builder-input" rows="4" maxlength="' +
              esc(String(cfg.max_chars || 400)) +
              '" placeholder="Edit your text…" aria-label="' +
              esc(field.label) +
              '" style="display:none;"></textarea>'
            : '') +
          '<div class="pb-ai-text-actions" style="display:none;">' +
          '<button type="button" class="builder-btn meta-btn pb-ai-text-regen">Generate again</button>' +
          '<button type="button" class="builder-btn meta-btn pb-ai-text-review-ack" style="display:none;">Looks good</button>' +
          '</div>';
      }
      innerHtml += hiddenInput + '</div>';
      const extraClasses = isContentOnly
        ? 'pb-step-hide-question pb-step-ai-text-content-only'
        : 'pb-step-hide-question';
      return {
        controlHtml: renderControlShell(field, innerHtml, ctx, { useQuestionHighlight: false }),
        imageHtml: renderAiTextPreviewStage(productImage),
        stepAttrs: ' data-ai-text="true" data-ai-text-layout="' + esc(layoutMode) + '"',
        extraClasses,
      };
    },
    initStep(stepEl) {
      delegateInitStep(stepEl, '.pb-ai-text-wrap', function (wrap) {
        // A newly rendered field belongs to this entry, even when its label was used before.
        const fresh = !wrap._pbAiTextState;
        const saved = fresh ? resolveAiTextDraftForContext()?.byLabel?.[wrap.dataset.label] : null;
        initWrap(wrap, { force: fresh, restoredState: deserializeAiTextFieldState(saved) });
      });
    },
    validateStep(stepEl) {
      const wrap = stepEl?.querySelector?.('.pb-ai-text-wrap');
      if (wrap) return getFieldReadiness(wrap).stepNavigable;
      return validateAiTextStep(stepEl);
    },
    getFieldReadiness(wrap) {
      return getFieldReadiness(wrap);
    },
    hydrateStep(stepEl) {
      this.initStep(stepEl);
    },
    flushStep(stepEl) {
      delegateInitStep(stepEl, '.pb-ai-text-wrap', function (wrap) {
        flushWrapFromDom(wrap);
      });
    },
  };
}

if (typeof window !== 'undefined') {
  window.PBFieldAiText = {
    computeAiTextReadiness,
    getFieldReadiness,
    parseAiTextFieldSettings,
    isAiTextField,
  };
}
